import * as React from 'react'
import { FieldProps, getIn } from 'formik'

import { TextFieldProps } from '@mui/material/TextField'
import { useCallback, useMemo, useState } from 'react'
import { PopperPlacementType } from '@mui/material/Popper'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import { CustomActionBar } from '@sales-center/components'
import { ENTITY_TYPES } from '@sales-center/common'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'

type Props = FieldProps & {
  disabled?: boolean
  disablePast?: boolean
  disableFuture?: boolean
  helperText?: string
  fullWidth?: boolean
  margin?: TextFieldProps['margin']
  required?: boolean
  placeholder?: string
  popperPlacement?: PopperPlacementType
  disableTether?: boolean
  clearable?: boolean
  isOpp?: boolean
  isSaveForLaterType?: boolean
}

export function MuiMobileDatePickerFormik(props: Props) {
  const {
    field,
    form: { isSubmitting, errors, touched, setFieldValue, setFieldTouched },
    disabled = isSubmitting,
    margin,
    fullWidth,
    helperText,
    required,
    popperPlacement,
    disableTether = false,
    clearable,
    isOpp = false,
    isSaveForLaterType = false,
    ...rest
  } = props

  const errorText = getIn(errors, field.name)
  const error = getIn(touched, field.name) && errorText !== undefined
  const { getFormat } = useLocalizationDateFormat()

  const [updateOtherDate, setUpdateOtherDate] = useState<boolean>(false)
  const [open, setOpen] = useState(false)
  const [previousValue, setPreviousValue] = useState(field.value)

  const onOpen = useCallback(() => setOpen(true), [])
  const onClose = useCallback(() => setOpen(false), [])

  const otherFieldName = useMemo(
    () =>
      field.name !== 'nextActionDate'
        ? 'nextActionDate'
        : 'expectedClosingDate',
    [field.name],
  )
  const handleChange = useCallback(
    (val) => {
      setPreviousValue(field.value)
      setFieldTouched(field.name, true)
      setFieldValue(field.name, val)
    },
    [field.name, field.value, setFieldTouched, setFieldValue],
  )

  const onClearDialog = useCallback(() => {
    onClose()
    if (
      (!isOpp || (isOpp && otherFieldName == 'expectedClosingDate')) &&
      field.value
    ) {
      setFieldTouched(field.name, true)
      setFieldValue(field.name, null)
    }
    if (isOpp) setUpdateOtherDate(false)
  }, [
    field.name,
    field.value,
    isOpp,
    onClose,
    otherFieldName,
    setFieldTouched,
    setFieldValue,
  ])
  const onAcceptDialog = useCallback(() => {
    onClose()
    if (isOpp && updateOtherDate && field.value) {
      if (!isSaveForLaterType) {
        setFieldTouched(otherFieldName, true)
        setFieldValue(otherFieldName, field.value)
      } else {
        setFieldTouched('shouldSyncNextActionDate', true)
        setFieldValue('shouldSyncNextActionDate', updateOtherDate)
      }
    }
    setUpdateOtherDate(false)
  }, [
    field.value,
    isOpp,
    isSaveForLaterType,
    onClose,
    otherFieldName,
    setFieldTouched,
    setFieldValue,
    updateOtherDate,
  ])

  const onCancelDialog = useCallback(() => {
    onClose()
    setFieldTouched(field.name, true)
    setFieldValue(field.name, previousValue)
    if (isSaveForLaterType) setFieldValue('shouldSyncNextActionDate', false)

    setUpdateOtherDate(false)
  }, [
    field.name,
    isSaveForLaterType,
    onClose,
    previousValue,
    setFieldTouched,
    setFieldValue,
  ])

  return (
    <MobileDatePicker
      {...field}
      {...rest}
      format={getFormat()}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      showToolbar={false}
      slots={{
        actionBar: CustomActionBar,
        toolbar: undefined,
      }}
      slotProps={{
        actionBar: {
          /*eslint-disable */
          //@ts-ignore
          isnextactiondate:
            field.name === 'nextActionDate' ? 'nextaction' : 'expectedclosing',
          setupdateotherdate: setUpdateOtherDate,
          disabled: !field.value,
          entitytype: isOpp ? ENTITY_TYPES.Opportunity : ENTITY_TYPES.Lead,
          onAccept: onAcceptDialog,
          onClear: onClearDialog,
          onCancel: onCancelDialog,
          actions:
            clearable && !required
              ? ['clear', 'cancel', 'accept']
              : ['cancel', 'accept'],
        },
        textField: {
          disabled: false,
          margin: margin,
          fullWidth: fullWidth,
          helperText: !!errorText && error ? errorText : helperText,
          required: required,
          onBlur: field.onBlur,
          name: field.name,
          error: error,
        },
      }}
      onChange={handleChange}
      disabled={disabled}
      onError={console.log}
      views={['day']}
    />
  )
}
